/**
 * TiptapStructuredEditor — Tiptap/ProseMirror-based document editor.
 *
 * Drop-in replacement for EditableStructuredPreview, gated behind
 * the `tiptap` feature flag (?tiptap=1). The legacy editor stays
 * the default until this component reaches feature parity.
 *
 * Phase status:
 *   ✅ Phase 1: foundation — Tiptap mounted, paper geometry preserved,
 *      starter-kit nodes, placeholder, flat seed from
 *      structured_content (h1/h2/h3 + bullet lists).
 *   ✅ Phase 2: structural typed nodes — Topic / TopicTitle /
 *      Bullet (with isDirectQuote + ambiguityFlag attrs). Topics
 *      now seed as proper schema-aware blocks; the rest of the
 *      doc (header, attendees, action items) still renders via
 *      generic headings + paragraphs until Phase 2.5.
 *   ✅ Phase 2.5: atom NodeViews for MeetingHeader,
 *      AttendeesBlock, ActionItemsBlock so typed fields (assignee,
 *      dueDate, isOjb) round-trip cleanly. Read-only render —
 *      edits still flow via legacy form pane.
 *   ✅ Phase 3: bidirectional JSON ↔ Tiptap doc sync. onUpdate
 *      debounces to 200ms, serializes the doc, and fires onChange
 *      so autosave persists in-editor edits. A useEffect on the
 *      content prop re-seeds the editor when an external change
 *      (form pane) lands — guarded by lastEmittedRef so the
 *      autosave round-trip doesn't reset the cursor mid-typing.
 *   ✅ Phase 4: BubbleMenu (Bold / Italic / Underline + AI rewrite)
 *      on text selection, plus Cmd+B/I/U keyboard shortcuts (auto-
 *      wired by StarterKit's Bold/Italic + the Underline extension).
 *      BulletNode also gets a React NodeView that restores the
 *      "Speaker:" chip we lost in Phase 3.
 *   ✅ Phase 4.5: inline editing of the atom NodeViews
 *      (MeetingHeader, AttendeesBlock, ActionItemsBlock). Every
 *      structural field (title, project, dates, attendee groups,
 *      action items, …) is now editable directly in the paper
 *      preview. Each NodeView calls updateAttributes() per
 *      keystroke, which routes through the editor's onUpdate →
 *      our debounced serializer → onChange → autosave.
 *   ✅ Phase 5: PaginatedPaper grows the sheet in 11" page-units
 *      as content gets taller. Per-page brand chrome (logo +
 *      office ribbon) repeats in the left margin strip; subtle
 *      "Page X of Y" perforations render between pages. PDF
 *      export still uses break-inside print CSS for crisp pages.
 *
 * The single Tiptap document gives the user the Word-doc feel:
 * click anywhere → cursor lands → type freely. Bullets, headings,
 * formatting all behave like Word. The schema-bound migration
 * happens in Phase 2 once the foundation is verified.
 */
import { useEffect, useMemo, useRef } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import { dropPoint } from '@tiptap/pm/transform';
import type { Slice } from '@tiptap/pm/model';
import type { StructuredContent } from '@ojb/shared';
import { PaginatedPaper } from './PaginatedPaper.tsx';
import {
  ActionItemNode,
  ActionItemsBlockNode,
  AttendeeGroupNode,
  AttendeesBlockNode,
  BulletNode,
  FirmTextNode,
  HeaderTitleNode,
  MeetingHeaderNode,
  MembersTextNode,
  TopicNode,
  TopicTitleNode,
} from './tiptap/nodes.ts';
import { proseMirrorDocToStructured } from './tiptap/serialize.ts';
import { TiptapBubbleMenu } from './tiptap/TiptapBubbleMenu.tsx';
import { TiptapTopToolbar } from './tiptap/TiptapTopToolbar.tsx';
import { Pagination } from './tiptap/Pagination.ts';
import { Reorder } from './tiptap/reorder.ts';

/** Debounce window for forwarding editor edits to onChange. Tiptap
 *  fires `update` on every transaction (per keystroke), so we
 *  coalesce them — the parent's autosave already debounces too,
 *  but trimming churn here cuts re-renders meaningfully. */
const ONCHANGE_DEBOUNCE_MS = 200;

interface Props {
  content: StructuredContent;
  onChange?: (next: StructuredContent) => void;
  readOnly?: boolean;
  showPageNumbers?: boolean;
  /** Required for the BubbleMenu's AI-rewrite call
   *  (POST /meetings/:id/rewrite). Passed through to TiptapBubbleMenu. */
  meetingId: string;
  /** Phase 5: fires whenever PaginatedPaper recomputes the page
   *  count (content height changed). Lets the parent surface the
   *  count in chrome outside the paper (e.g. the document-stats
   *  badge in the zoom toolbar). Optional — paper still paginates
   *  visually even if the parent doesn't subscribe. */
  onPageCountChange?: (pageCount: number) => void;
  /**
   * Optional live override for the meeting header's `project` attr.
   * When set, the editor calls editor.commands.updateAttributes
   * surgically on the meetingHeader node instead of forcing a full
   * doc re-seed via setContent. Used by EditorRoute to mirror the
   * user's project-alias edits into the right-pane preview without
   * touching draft.structuredContent (which would trigger the
   * external-content useEffect and crash the editor with the
   * "removeChild on Node" reconciliation error). Pass `undefined`
   * (or omit) when you DON'T want a live override — the project
   * value baked into `content.project` is used as-is.
   */
  projectOverride?: string;
}

/** Convert a structured_content snapshot to Tiptap's ProseMirror
 *  JSON shape. Phase 6 free-flow tree:
 *
 *    meetingHeader {project, meetingDate, ..., format, variant}
 *      └─ headerTitle [text*]
 *    attendeesBlock
 *      └─ attendeeGroup* {isOjb}
 *           ├─ firmText [text*]
 *           └─ membersText [text*]   (comma-separated)
 *    actionItemsBlock
 *      └─ actionItem* {assigneeName, dueDate, status} [text*]
 *    topic+
 *      ├─ topicTitle [text*]
 *      └─ bullet+ {speaker, isDirectQuote, ambiguityFlag} [text*]
 *
 *  Every text region is contenteditable. Cursor / selection /
 *  marks all work uniformly across the doc.
 */
function structuredToProseMirrorDoc(content: StructuredContent) {
  const nodes: Array<Record<string, unknown>> = [];

  // meetingHeader — non-atom block. Title is an editable headerTitle
  // child; meta fields stay as attrs and render via the NodeView's
  // pill row.
  nodes.push({
    type: 'meetingHeader',
    attrs: {
      project: content.project ?? '',
      meetingDate: content.meetingDate ?? '',
      notesDate: content.notesDate ?? '',
      location: content.location ?? '',
      meetingFormat: content.meetingFormat ?? 'in_person',
      templateVariant: content.templateVariant ?? 'client_meeting',
    },
    content: [
      {
        type: 'headerTitle',
        content: content.title
          ? [{ type: 'text', text: content.title }]
          : [],
      },
    ],
  });

  // attendeesBlock — non-atom block containing one attendeeGroup
  // child per AttendeeGroup in the source. Always emit (even when
  // empty) so the editable view can show "+ Add attendees" buttons.
  nodes.push({
    type: 'attendeesBlock',
    content: (content.attendeeGroups ?? []).map((g) => ({
      type: 'attendeeGroup',
      attrs: { isOjb: g.isOjb === true },
      content: [
        {
          type: 'firmText',
          content: g.firm ? [{ type: 'text', text: g.firm }] : [],
        },
        {
          type: 'membersText',
          content:
            g.members && g.members.length > 0
              ? // Join members with `; ` (semicolon) — commas are
                // reserved for credentials inside a single member
                // entry ("Andrew Cridlin, ASLA"). The serializer
                // (serialize.ts) splits on `;` to invert this.
                [{ type: 'text', text: g.members.join('; ') }]
              : [],
        },
      ],
    })),
  });

  // actionItemsBlock — non-atom block containing one actionItem
  // child per ActionItem.
  nodes.push({
    type: 'actionItemsBlock',
    content: (content.actionItems ?? []).map((it) => ({
      type: 'actionItem',
      attrs: {
        assigneeName: it.assigneeName ?? null,
        dueDate: it.dueDate ?? null,
        status: it.status ?? 'open',
      },
      content: it.description
        ? [{ type: 'text', text: it.description }]
        : [],
    })),
  });

  // Topics → typed `topic` nodes with topicTitle + bullet+ children.
  for (const topic of content.topics ?? []) {
    const topicTitle = (topic.title ?? '').trim();
    const sourceBullets =
      topic.bullets && topic.bullets.length > 0
        ? topic.bullets
        : [{ body: '', isDirectQuote: false, ambiguityFlag: undefined }];

    nodes.push({
      type: 'topic',
      attrs: { key: topic.kind ?? null },
      content: [
        {
          type: 'topicTitle',
          content: topicTitle
            ? [{ type: 'text', text: topicTitle }]
            : [],
        },
        ...sourceBullets.map((b) => ({
          type: 'bullet',
          attrs: {
            isDirectQuote: b.isDirectQuote ?? false,
            ambiguityFlag: b.ambiguityFlag ?? null,
            speaker: b.speaker ?? null,
            key: null,
          },
          content: b.body ? [{ type: 'text', text: b.body }] : [],
        })),
      ],
    });
  }

  return { type: 'doc', content: nodes };
}

export function TiptapStructuredEditor({
  content,
  onChange,
  readOnly = false,
  showPageNumbers = true,
  meetingId,
  onPageCountChange,
  projectOverride,
}: Props) {
  // Phase 3 sync state.
  //   contentRef holds the latest content prop so the debounced
  //     update handler can use it as a fallback baseline without
  //     stale-closure bugs.
  //   lastEmittedRef holds JSON.stringify of the last content we
  //     emitted via onChange. We compare incoming content props to
  //     it: if they match, the prop change is our own autosave
  //     round-trip → skip re-seed (cursor stays put). If they
  //     differ, an external edit (form pane) landed → re-seed.
  //   onChangeRef so the editor's update callback always invokes
  //     the current onChange without re-creating the editor.
  //   debounceRef tracks the pending setTimeout so we can clear
  //     on unmount and on fresh updates.
  const contentRef = useRef(content);
  contentRef.current = content;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const lastEmittedRef = useRef<string>(JSON.stringify(content));
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const initialDoc = useMemo(
    () => structuredToProseMirrorDoc(content),
    // Seed only on mount. Subsequent prop changes go through the
    // useEffect below, which decides whether to re-seed (external
    // edit) or skip (our own autosave round-trip).
    [],
  );

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Disable a couple of starter nodes we won't ship in v1.
        // codeBlock + horizontalRule add visual noise that doesn't
        // match the meeting-minutes aesthetic.
        codeBlock: false,
        horizontalRule: false,
        // Style the drop cursor with our pine accent so it reads
        // as on-brand instead of the default browser blue. The
        // visible line is what prosemirror-dropcursor renders
        // during dragover; users see it as the drop indicator.
        dropcursor: { color: '#375542', width: 2 },
      }),
      Placeholder.configure({
        placeholder: 'Start typing your meeting notes…',
      }),
      // Phase 4: Cmd+U + a renderable underline mark. Bold/Italic
      // are already in StarterKit, so this completes the B/I/U set
      // exposed in the BubbleMenu and via keyboard shortcuts.
      Underline,
      // Phase 6 — structural editable nodes. Every block holds
      // contenteditable text* children, so cursor / marks /
      // selection work uniformly across the entire document.
      MeetingHeaderNode,
      HeaderTitleNode,
      AttendeesBlockNode,
      AttendeeGroupNode,
      FirmTextNode,
      MembersTextNode,
      ActionItemsBlockNode,
      ActionItemNode,
      TopicNode,
      TopicTitleNode,
      BulletNode,
      // Phase 7: enforces real page boundaries. Walks top-level
      // blocks, measures heights, and inserts non-editable spacer
      // widgets when a block would cross into the bottom-margin
      // area. Top + bottom margins are now respected on every
      // page in the editor (PDF export already paginates via
      // Chromium's print stylesheet).
      Pagination,
      // Alt+↑ / Alt+↓ keyboard reorder for topics, bullets,
      // action items, and attendee groups. Mirrors the up/down
      // arrow buttons rendered by ReorderControls in each
      // NodeView's left-margin gutter.
      Reorder,
    ],
    content: initialDoc,
    editable: !readOnly,
    onUpdate: ({ editor: ed }) => {
      // Coalesce per-keystroke transactions into one onChange per
      // ONCHANGE_DEBOUNCE_MS window. The serializer is cheap, but
      // the parent's setDraft cascades into many renders, so we'd
      // rather pay one of those per ~typing pause than one per
      // keystroke.
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        const cb = onChangeRef.current;
        if (!cb) return;
        const next = proseMirrorDocToStructured(
          ed.getJSON(),
          contentRef.current,
        );
        const nextStr = JSON.stringify(next);
        // Skip identical emits — happens when a transaction (e.g.
        // mark toggle, cursor move via input rule) doesn't change
        // any structured field.
        if (nextStr === lastEmittedRef.current) return;
        lastEmittedRef.current = nextStr;
        cb(next);
      }, ONCHANGE_DEBOUNCE_MS);
    },
    editorProps: {
      attributes: {
        // Tiptap renders a contenteditable div; we use arbitrary
        // descendant selectors so we don't need @tailwindcss/
        // typography. After Phase 2.5, header / attendees / action
        // items are atom NodeViews that bring their own Tailwind
        // classes — these h1/h3/p/ul rules now only apply when the
        // user invokes StarterKit's heading/list commands inside
        // their own typed prose (e.g. inside a topic body bullet).
        class:
          // Base 12px — middle ground between the original 11px
          // and the bumped-too-large 14px. Brand-template smaller
          // sizes (7pt body etc.) apply only in PDF/.docx export.
          'tiptap-editor focus:outline-none text-[12px] text-charcoal ' +
          // h1 / h3 / p / ul styling for any free-form headings or
          // lists the user creates via StarterKit commands.
          '[&_h1]:mt-0 [&_h1]:mb-2 [&_h1]:text-[18px] [&_h1]:font-semibold [&_h1]:tracking-tight [&_h1]:text-charcoal ' +
          '[&_h3]:mt-3 [&_h3]:mb-1 [&_h3]:text-[10px] [&_h3]:font-semibold [&_h3]:uppercase [&_h3]:tracking-[0.14em] [&_h3]:text-pine ' +
          '[&_p]:my-1 [&_p]:leading-relaxed ' +
          '[&_ul]:my-1 [&_ul]:pl-5 [&_ul]:list-disc [&_li]:my-0.5 ' +
          // Phase 6 structural blocks — meetingHeader, attendeesBlock,
          // actionItemsBlock and their children. All wrapper divs;
          // children are NodeViewContent regions (text*) that flow
          // as normal contenteditable.
          '[&_.tt-meeting-header]:mb-4 ' +
          '[&_.tt-header-title]:mt-0 [&_.tt-header-title]:mb-2 [&_.tt-header-title]:text-[18px] [&_.tt-header-title]:font-semibold [&_.tt-header-title]:tracking-tight [&_.tt-header-title]:text-charcoal ' +
          '[&_.tt-header-title:empty]:before:content-["Untitled_meeting"] [&_.tt-header-title:empty]:before:text-charcoal [&_.tt-header-title:empty]:before:italic ' +
          '[&_.tt-attendees-block]:mb-3 ' +
          '[&_.tt-attendee-group]:my-0.5 ' +
          '[&_.tt-firm-text]:font-semibold ' +
          // ": " separator after firm text, only when non-empty.
          // mr-2 gives a clear visual gap between the bolded firm
          // name and the comma-separated members text.
          '[&_.tt-firm-text:not(:empty)]:after:content-[":"] [&_.tt-firm-text:not(:empty)]:after:mr-2 [&_.tt-firm-text:not(:empty)]:after:font-normal [&_.tt-firm-text:not(:empty)]:after:text-charcoal ' +
          '[&_.tt-firm-text:empty]:before:content-["Firm"] [&_.tt-firm-text:empty]:before:text-charcoal [&_.tt-firm-text:empty]:before:italic ' +
          '[&_.tt-members-text:empty]:before:content-["Type_names_separated_by_semicolons"] [&_.tt-members-text:empty]:before:text-charcoal [&_.tt-members-text:empty]:before:italic ' +
          '[&_.tt-action-items]:mb-3 ' +
          '[&_.tt-action-item]:my-0.5 [&_.tt-action-item]:list-none ' +
          '[&_.tt-action-item__description:empty]:before:content-["Action_item…"] [&_.tt-action-item__description:empty]:before:text-charcoal [&_.tt-action-item__description:empty]:before:italic ' +
          // Topic + topic-title + bullet (unchanged from Phase 2).
          '[&_.tt-topic]:mt-4 [&_.tt-topic]:mb-3 ' +
          '[&_.tt-topic-title]:mt-0 [&_.tt-topic-title]:mb-1.5 [&_.tt-topic-title]:text-[14px] [&_.tt-topic-title]:font-semibold [&_.tt-topic-title]:uppercase [&_.tt-topic-title]:tracking-[0.12em] [&_.tt-topic-title]:text-pine ' +
          // Bullets render with a pine disc glyph in the left margin
          // via a CSS pseudo-element so we keep ProseMirror's
          // contentEditable surface as a clean <div>.
          '[&_.tt-bullet]:relative [&_.tt-bullet]:my-0.5 [&_.tt-bullet]:pl-4 [&_.tt-bullet]:leading-relaxed ' +
          '[&_.tt-bullet]:before:absolute [&_.tt-bullet]:before:left-1 [&_.tt-bullet]:before:top-[0.55em] [&_.tt-bullet]:before:h-1 [&_.tt-bullet]:before:w-1 [&_.tt-bullet]:before:rounded-full [&_.tt-bullet]:before:bg-pine/70 ' +
          // Direct quotes get italic styling (legacy convention)
          '[&_.tt-bullet--quote]:italic [&_.tt-bullet--quote]:text-charcoal ' +
          // Speaker prefix renders via BulletNodeView (Phase 4) — a
          // non-editable <strong>Alice:</strong> in front of the
          // editable body. Keeping the data-speaker attr + .tt-bullet--
          // speaker class on the wrapper so future CSS hooks have
          // something to target.

          // Ambiguity-flag chip used to render here via a CSS
          // ::after rule sourcing data-ambiguity. Replaced by an
          // interactive React chip rendered inside BulletNodeView
          // itself so a pilot user can click the flag → popover
          // with Dismiss + Edit actions. The .tt-bullet--flag
          // class is kept on the wrapper for any legacy CSS
          // hooks but no longer drives any rendering.

          // Empty paragraph placeholder (Tiptap Placeholder ext)
          '[&_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)] ' +
          '[&_p.is-editor-empty:first-child::before]:float-left ' +
          '[&_p.is-editor-empty:first-child::before]:text-charcoal ' +
          '[&_p.is-editor-empty:first-child::before]:pointer-events-none ' +
          '[&_p.is-editor-empty:first-child::before]:h-0 ' +
          // Source-dim — applied by useDragHandle via classList on
          // dragstart, removed on dragend. Gives the user a clear
          // visual answer to "what am I dragging?" while the
          // ghost is in flight.
          '[&_.tt-dragging-source]:opacity-40 ' +
          '[&_.tt-dragging-source]:outline-dashed ' +
          '[&_.tt-dragging-source]:outline-1 ' +
          '[&_.tt-dragging-source]:outline-pine ' +
          '[&_.tt-dragging-source]:outline-offset-2',
      },
      // Permissive drag-over + custom drop handler.
      //
      // Why we own these end-to-end (instead of letting PM
      // default through):
      //
      //   - PM's default dragover only preventDefaults the event
      //     when its strict schema-aware dropPoint() returns a
      //     valid position. For our doc shape (topic >
      //     topicTitle + bullets, actionItemsBlock > actionItem,
      //     etc.) dropPoint frequently returns null when the
      //     cursor is mid-block, between topics, or over a
      //     non-matching parent. That makes the browser render
      //     the red ⊘ "not allowed" cursor and the drop event
      //     never fires.
      //
      //   - PM's default drop relies on a NodeSelection over the
      //     source node to know what to delete on a "move".
      //     Setting that selection during dragstart fights React
      //     NodeView re-renders + BubbleMenu surfacing, leaving
      //     the drop silently broken for many positions ("shows
      //     the line where it might go, but then nothing
      //     happens").
      //
      // Solution:
      //
      //   dragover — always preventDefault + set dropEffect=move
      //     while view.dragging is populated, so the drop event
      //     fires regardless of schema fit.
      //
      //   drop — read the slice + from/to source range directly
      //     off view.dragging (stashed by useDragHandle in
      //     dragHandle.ts), compute a schema-valid insertion
      //     point with dropPoint() falling back to the raw cursor
      //     coords, then build a single transaction that deletes
      //     the source range and inserts the slice at the
      //     adjusted target. Returning true tells PM not to run
      //     its own default drop afterward.
      handleDOMEvents: {
        dragover: (view, event) => {
          const draggingView = view as unknown as {
            dragging?: { slice: Slice; move: boolean } | null;
          };
          if (draggingView.dragging) {
            event.preventDefault();
            if (event.dataTransfer) {
              event.dataTransfer.dropEffect = 'move';
            }
          }
          return false; // allow PM's own dragover to also run
        },
        drop: (view, event) => {
          const draggingView = view as unknown as {
            dragging?:
              | { slice: Slice; move: boolean; from: number; to: number }
              | null;
          };
          const dragging = draggingView.dragging;
          // Not our drag (e.g. text drop from another tab) — let
          // PM's default drop handle it.
          if (!dragging) return false;

          // Always clear so a stale slice can't survive a partial
          // drop. We've now consumed view.dragging; from this
          // point on PM's default drop sees nothing and falls
          // through.
          draggingView.dragging = null;

          const eventPos = view.posAtCoords({
            left: event.clientX,
            top: event.clientY,
          });
          if (!eventPos) return false;

          const { slice, from, to } = dragging;

          // Find a schema-valid insertion point. dropPoint walks
          // up from the cursor position until it finds a parent
          // that accepts the slice's content — exactly the
          // search PM's default drop does internally.
          let insertPos = dropPoint(view.state.doc, eventPos.pos, slice);
          if (insertPos == null) {
            // Schema can't fit the slice anywhere reasonable from
            // the cursor. Fall back to the raw cursor pos as a
            // best effort; the insert may still throw, in which
            // case we bail in the catch below.
            insertPos = eventPos.pos;
          }

          // Schema-aware walk-up validator. The INLINE_ONLY
          // hardcoded list this replaces was insufficient — when
          // the user dropped on a sibling bullet's text, the
          // resolved position landed inside the bullet (which
          // can't accept another bullet) and the insert threw.
          //
          // This generic version asks the schema directly: at
          // the current depth, can the parent accept the slice's
          // content here? If yes, use the position. If no, walk
          // up by bumping to the position right after the
          // current parent (so the next iteration evaluates the
          // grandparent). Capped at 6 hops so a pathological doc
          // can't trap us in an infinite loop.
          const doc = view.state.doc;
          let bumpedInsertPos = insertPos;
          for (let i = 0; i < 6; i++) {
            const $at = doc.resolve(bumpedInsertPos);
            const parent = $at.parent;
            const idx = $at.index();
            if (parent.canReplace(idx, idx, slice.content)) break;
            if ($at.depth === 0) break; // already at doc, give up
            bumpedInsertPos = $at.after();
          }
          insertPos = bumpedInsertPos;

          // Dropping inside the source range is a no-op. Consume
          // the event so the browser doesn't perform any default
          // text-drop fallback, then bail.
          if (insertPos >= from && insertPos <= to) {
            event.preventDefault();
            return true;
          }

          event.preventDefault();

          let tr = view.state.tr;
          // Delete the source range first, then map the target
          // through the deletion before inserting. Doing it in
          // this order means positions BEFORE the deletion stay
          // unchanged, and positions AFTER shift by the deleted
          // length — handled automatically via tr.mapping.
          tr = tr.delete(from, to);
          const mappedInsertPos = tr.mapping.map(insertPos, -1);

          try {
            tr = tr.insert(mappedInsertPos, slice.content);
          } catch (err) {
            // Schema rejected the insert at the mapped position.
            // Don't dispatch a half-move (that would delete the
            // source without re-inserting it). Returning false
            // lets PM try its own default drop, which will most
            // likely also fail — at least the browser cancels
            // cleanly instead of the user losing the source.
            console.warn('[tiptap] drop insert rejected by schema', err);
            return false;
          }

          view.dispatch(tr.scrollIntoView());
          return true;
        },
      },
    },
  });

  // NOTE on readOnly transitions:
  //   We deliberately do NOT call editor.setEditable(!readOnly) at
  //   runtime when the prop changes. Doing so triggers Tiptap to
  //   re-render every NodeView with a new isEditable value, and the
  //   resulting DOM mutations collide with React's reconciliation
  //   during the same commit — that's the "removeChild on Node"
  //   crash users hit when clicking Finalize.
  //
  //   Instead, the parent (EditorRoute) is responsible for forcing
  //   a full editor re-mount on readOnly change via the React `key`
  //   prop (see EditorRoute.tsx where TiptapStructuredEditor is
  //   instantiated). React tears down the old editor instance,
  //   creates a new one with `editable: !readOnly` baked in at
  //   construction, and seeds it from the current content prop.
  //   Cursor position and undo history are lost in the transition,
  //   but that's acceptable on a finalize/draft toggle (the user
  //   is changing editing modes, so a clean re-mount is sensible).

  // Surgical project-name override. When the parent passes a
  // projectOverride prop (because the user is typing in the
  // project-alias field on the meeting-details panel), we update
  // the meetingHeader node's project attribute via a single PM
  // transaction — NOT a full doc re-seed. This is the safe,
  // crash-free way to live-update the right-pane preview without
  // triggering the React/ProseMirror DOM ownership conflict that
  // setContent causes. updateAttributes touches one node's attrs;
  // the surrounding doc structure is unchanged so React's
  // reconciliation has no stale references.
  // Debounced via state timer so rapid keystrokes coalesce into
  // one transaction per typing pause — keeps the editor smooth
  // and the autosave history clean.
  useEffect(() => {
    if (!editor || projectOverride === undefined) return;
    const t = window.setTimeout(() => {
      // Locate the meetingHeader node — it's always the first
      // top-level child of the doc in our schema.
      const tr = editor.state.tr;
      let pos: number | null = null;
      editor.state.doc.descendants((node, p) => {
        if (pos !== null) return false;
        if (node.type.name === 'meetingHeader') {
          pos = p;
          return false;
        }
        return true;
      });
      if (pos === null) return;
      const headerNode = editor.state.doc.nodeAt(pos);
      if (!headerNode) return;
      const currentProject = (headerNode.attrs as { project?: string })
        .project ?? '';
      if (currentProject === projectOverride) return;
      tr.setNodeMarkup(pos, undefined, {
        ...headerNode.attrs,
        project: projectOverride,
      });
      // setMeta('addToHistory', false) keeps undo/redo focused on
      // the user's content edits; alias-mirror updates aren't
      // worth burning history slots.
      tr.setMeta('addToHistory', false);
      editor.view.dispatch(tr);
    }, 200);
    return () => window.clearTimeout(t);
  }, [editor, projectOverride]);

  // External-content sync: when the content prop changes from a
  // source other than our own onUpdate (e.g. a form-pane edit, a
  // server-side AI rewrite, a meeting load, a successful
  // re-transform), re-seed the editor so the preview reflects
  // it. The lastEmittedRef guard skips re-seeding for our own
  // autosave round-trip — that lets the user keep typing
  // without the cursor jumping back to position 0 on every
  // persisted save.
  //
  // setContent can throw if the new structured_content has any
  // schema mismatch with our PM doc spec (e.g. a topic whose
  // bullets list shape doesn't fit topicTitle bullet*). The
  // throw escapes the useEffect and React unmounts the parent
  // tree — which is exactly the "page goes blank after
  // re-transform" pilot bug. Catching here keeps the editor
  // mounted with the previous content and surfaces the error
  // in the console for triage; the parent error boundary
  // (StructuredEditorErrorBoundary, below) catches anything
  // synchronous from initial render too.
  useEffect(() => {
    if (!editor) return;
    const incoming = JSON.stringify(content);
    if (incoming === lastEmittedRef.current) return;
    lastEmittedRef.current = incoming;
    try {
      // setContent(parsedDoc, false) — the `false` suppresses
      // the onUpdate emit so we don't loop back into onChange.
      editor.commands.setContent(
        structuredToProseMirrorDoc(content),
        false,
      );
    } catch (err) {
      console.error(
        '[TiptapStructuredEditor] setContent threw on external sync; keeping previous editor state.',
        err,
        { incomingPreview: incoming.slice(0, 200) },
      );
      // Reset lastEmittedRef so a subsequent (corrected) content
      // change can attempt the re-seed again. Without this we'd
      // be permanently stuck showing the pre-error doc until
      // the user reloads.
      lastEmittedRef.current = '';
    }
  }, [content, editor]);

  // Flush any pending debounced emit on unmount so the parent
  // doesn't lose the last few keystrokes when the editor closes.
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, []);

  // Auto-scroll near viewport edges while a drag is in flight.
  // Without this, dragging a topic from page 1 to page 4 of a
  // long doc requires the user to interrupt the drag, scroll
  // manually, then start over — which is a known bad-UX cliff
  // for native HTML5 drag. While view.dragging is set, watch
  // dragover events at the document level; if the cursor is
  // within SCROLL_ZONE pixels of the top or bottom of the
  // viewport, scroll the page on each animation frame until the
  // cursor moves out of the zone or drag ends.
  useEffect(() => {
    if (!editor) return;
    const SCROLL_ZONE = 80;
    const SCROLL_SPEED = 14;
    let rafId: number | null = null;
    let pendingY: number | null = null;

    const tick = () => {
      rafId = null;
      const draggingView = editor.view as unknown as { dragging?: unknown };
      if (!draggingView.dragging || pendingY == null) return;
      const y = pendingY;
      const vh = window.innerHeight;
      const scroller =
        (document.scrollingElement as HTMLElement | null) ||
        document.documentElement;
      if (y < SCROLL_ZONE) {
        scroller.scrollTop -= SCROLL_SPEED;
        rafId = window.requestAnimationFrame(tick);
      } else if (y > vh - SCROLL_ZONE) {
        scroller.scrollTop += SCROLL_SPEED;
        rafId = window.requestAnimationFrame(tick);
      }
    };

    const onDragOver = (e: DragEvent) => {
      const draggingView = editor.view as unknown as { dragging?: unknown };
      if (!draggingView.dragging) return;
      pendingY = e.clientY;
      if (rafId == null) rafId = window.requestAnimationFrame(tick);
    };

    const cancel = () => {
      pendingY = null;
      if (rafId != null) {
        window.cancelAnimationFrame(rafId);
        rafId = null;
      }
    };

    document.addEventListener('dragover', onDragOver);
    document.addEventListener('drop', cancel);
    document.addEventListener('dragend', cancel);
    return () => {
      document.removeEventListener('dragover', onDragOver);
      document.removeEventListener('drop', cancel);
      document.removeEventListener('dragend', cancel);
      cancel();
    };
  }, [editor]);

  return (
    <>
      {/* Phase 6: persistent floating toolbar above the paper.
          Always visible (when editable) so the user has Word-style
          access to bold/italic/underline/headings/lists/AI-rewrite
          regardless of selection. */}
      {!readOnly && <TiptapTopToolbar editor={editor} meetingId={meetingId} />}

      <PaginatedPaper
        onPageCountChange={onPageCountChange}
        // Pass title + date so PaginatedPaper can render a small
        // running header on pages 2+ (logo + title + date in the
        // left margin strip). Page 1 still gets the full
        // letterhead with the office ribbon.
        meetingTitle={content.title}
        meetingDate={content.meetingDate}
        showPageNumber={showPageNumbers}
      >
        <EditorContent editor={editor} className="h-full" />
        {/* Floating selection toolbar — only renders when the user has
            a non-empty text selection inside the editor. */}
        {!readOnly && (
          <TiptapBubbleMenu editor={editor} meetingId={meetingId} />
        )}
      </PaginatedPaper>
    </>
  );
}
